import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CurrencyDollarIcon, ArrowUUpLeftIcon } from '@phosphor-icons/react';
import { useCharge } from '@/api/rpc/hooks/finance/useCharges';
import type { Charge } from '@erp/rpc';

/** Fields from Charge used for preview display */
type ChargePreviewData = Pick<
  Charge,
  | 'amount'
  | 'currencyCode'
  | 'chargeCategory'
  | 'direction'
  | 'isEstimate'
  | 'docLineId'
  | 'reversesChargeId'
>;

interface ChargePreviewProps {
  entityId: string;
  className?: string;
  // Optional: Pass charge data directly to avoid extra fetching
  chargeData?: ChargePreviewData;
}

function formatCurrency(amount: string, currencyCode: string): string {
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) {
    return amount;
  }

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });
}

export function ChargePreview({
  entityId,
  className,
  chargeData: passedChargeData,
}: ChargePreviewProps) {
  // Fetch charge data if not passed directly
  const { charge: fetchedCharge, isLoading } = useCharge({
    chargeId: entityId,
    enabled: !passedChargeData,
  });

  // Use passed data if available, otherwise use fetched data
  const chargeData = passedChargeData ?? fetchedCharge;

  if (isLoading && !chargeData) {
    return (
      <div className={cn('min-w-56 max-w-sm p-2', className)}>
        <p className="text-xs text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!chargeData) {
    return (
      <div className={cn('min-w-56 max-w-sm p-2', className)}>
        <p className="text-xs text-muted-foreground">Charge not found</p>
      </div>
    );
  }

  const isReversal = !!chargeData.reversesChargeId;

  let amountFormatted = formatCurrency(
    chargeData.amount,
    chargeData.currencyCode,
  );
  // Show CREDIT charges in financial negative notation (parentheses)
  if (chargeData.direction === 'CREDIT') {
    amountFormatted = `(${amountFormatted})`;
  }

  const categoryLabel =
    chargeData.chargeCategory?.label ||
    chargeData.chargeCategory?.value ||
    'Charge';

  const directionVariant: 'secondary' | 'outline' =
    chargeData.direction === 'CREDIT' ? 'secondary' : 'outline';

  const isHeaderCharge = !chargeData.docLineId;

  return (
    <div className={cn('min-w-56 max-w-sm p-2', className)}>
      <div className="flex items-center gap-2 mb-1">
        <CurrencyDollarIcon className="size-4 text-muted-foreground" />
        <h4
          className={cn(
            'text-sm font-semibold',
            isReversal && 'text-amber-700',
          )}
        >
          {categoryLabel}
        </h4>
        {isReversal && (
          <Badge
            variant="outline"
            className="text-[10px] py-0.5 px-1.5 border-amber-500 text-amber-700 bg-amber-50"
          >
            <ArrowUUpLeftIcon className="size-2.5 mr-0.5" />
            Reversal
          </Badge>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div
          className={cn(
            'font-mono font-semibold',
            isReversal && 'text-amber-700',
          )}
        >
          {amountFormatted}
        </div>
        <div className="flex gap-1">
          <Badge
            variant={directionVariant}
            className="text-[10px] py-0.5 px-1.5"
          >
            {chargeData.direction}
          </Badge>
          {chargeData.isEstimate && (
            <Badge variant="secondary" className="text-[10px] py-0.5 px-1.5">
              EST
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        <span>{isHeaderCharge ? 'Header Charge' : 'Line Charge'}</span>
      </div>
    </div>
  );
}
